import { useState } from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { PageBanner } from "@/shared/components/PageBanner";
import { useAddRotationSlot, useCreateStationAssignment, useOperatorStationAssignments, useResolveStation } from "../api";
import { useV1UsersList, useV1ProductionWorkCentersList, useV1ProductionMachinesList } from "@/api/generated/v1/v1";
import type { AssignmentMode } from "../types";

const MODE_LABELS: Record<AssignmentMode, string> = {
  sole: "انحصاری (کل شیفت روی یک ایستگاه)",
  concurrent: "هم‌زمان (چند دستگاه هم‌زمان)",
  rotational: "چرخشی (در طول شیفت جابه‌جا می‌شود)",
};

export default function StationAssignmentPage() {
  const { data: assignments = [] } = useOperatorStationAssignments();
  const create = useCreateStationAssignment();
  const addSlot = useAddRotationSlot();

  const { data: usersData } = useV1UsersList({ page_size: 1000 } as any);
  const { data: wcData } = useV1ProductionWorkCentersList({ page_size: 1000 } as any);
  const { data: machinesData } = useV1ProductionMachinesList({ page_size: 1000 } as any);

  const users = usersData?.results || [];
  const workCenters = wcData?.results || [];
  const machines = machinesData?.results || [];

  const [form, setForm] = useState({
    operator_user_id: "",
    mode: "sole" as AssignmentMode,
    start_date: "",
    target_work_center: "",
    target_machine: "",
  });

  const [slotForm, setSlotForm] = useState({
    assignment: "",
    sequence_no: 1,
    relative_start_hours: 0,
    relative_end_hours: 1,
    target_work_center: "",
    target_machine: "",
  });

  const [lookup, setLookup] = useState({ operator_user_id: "", date: "" });
  const { data: resolved = [] } = useResolveStation(lookup.operator_user_id, lookup.date, undefined, undefined, !!lookup.operator_user_id && !!lookup.date);

  const toIsoDuration = (hours: number) => `PT${hours}H`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem", fontFamily: "var(--font-family-fa, sans-serif)" }} dir="rtl">
      <PageBanner
        title="تخصیص ایستگاه و دستگاه"
        subtitle="مدیریت تخصیص اپراتورها به ایستگاه‌های کاری یا ماشین‌آلات (انحصاری، هم‌زمان یا چرخشی)"
        icon="🛠️"
        helpLink="/help/shopfloor/station-assignments"
      />

      <section className="brand-card-extand" style={{ padding: "1.5rem", border: "1px solid var(--color-border, #E4DCC8)", display: "flex", flexDirection: "column", gap: "1rem" }}>
        <h2 style={{ fontSize: "1.1rem", fontWeight: 800, color: "#007270", marginBottom: "0.5rem" }}>تخصیص دوره‌ای جدید</h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "flex-end" }}>
          <div style={{ flex: "1 1 200px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>اپراتور</label>
            <select
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", cursor: "pointer" }}
              value={form.operator_user_id}
              onChange={(e) => setForm((s) => ({ ...s, operator_user_id: e.target.value }))}
            >
              <option value="">انتخاب کنید...</option>
              {users.map((u: any) => (
                <option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name} ({u.username})
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1 1 200px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>حالت تخصیص</label>
            <select
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", cursor: "pointer" }}
              value={form.mode}
              onChange={(e) => setForm((s) => ({ ...s, mode: e.target.value as AssignmentMode }))}
            >
              {Object.entries(MODE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1 1 150px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>ایستگاه کاری</label>
            <select
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", cursor: "pointer" }}
              value={form.target_work_center}
              onChange={(e) => setForm((s) => ({ ...s, target_work_center: e.target.value, target_machine: "" }))}
            >
              <option value="">(بدون ایستگاه)</option>
              {workCenters.map((wc: any) => (
                <option key={wc.id} value={wc.id}>
                  {wc.name} ({wc.code})
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1 1 150px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>دستگاه</label>
            <select
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", cursor: "pointer" }}
              value={form.target_machine}
              onChange={(e) => setForm((s) => ({ ...s, target_machine: e.target.value, target_work_center: "" }))}
            >
              <option value="">(بدون دستگاه)</option>
              {machines.map((m: any) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.code})
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1 1 150px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>تاریخ شروع</label>
            <DatePicker
              calendar={persian}
              locale={persian_fa}
              calendarPosition="bottom-right"
              portal
              value={form.start_date || ""}
              onChange={(date: DateObject | null) => {
                setForm((s) => ({ ...s, start_date: date ? date.format("YYYY-MM-DD") : "" }));
              }}
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", height: "auto" }}
              containerStyle={{ width: "100%" }}
              placeholder="انتخاب تاریخ شمسی..."
            />
          </div>
          <div style={{ flex: "0 0 auto" }}>
            <button
              style={{ padding: "8px 16px", borderRadius: "10px", background: "#007270", color: "#FFF", border: "none", fontSize: "0.85rem", fontWeight: 700, cursor: create.isPending ? "not-allowed" : "pointer", opacity: create.isPending ? 0.7 : 1 }}
              onClick={() =>
                create.mutate({
                  operator_user_id: form.operator_user_id,
                  mode: form.mode,
                  start_date: form.start_date,
                  target_work_center: form.target_work_center || undefined,
                  target_machine: form.target_machine || undefined,
                })
              }
              disabled={create.isPending || !form.operator_user_id || !form.start_date}
            >
              تخصیص ایستگاه
            </button>
          </div>
        </div>
        <p style={{ fontSize: "0.75rem", color: "#888", margin: 0 }}>
          دقیقاً یکی از «ایستگاه» یا «دستگاه» را پر کنید. برای حالت هم‌زمان (concurrent) می‌توانید چند تخصیص هم‌زمان برای یک نفر ثبت کنید.
        </p>
      </section>

      <section className="brand-card-extand" style={{ padding: "1.5rem", border: "1px solid var(--color-border, #E4DCC8)", display: "flex", flexDirection: "column", gap: "1rem" }}>
        <h2 style={{ fontSize: "1.1rem", fontWeight: 800, color: "#007270", marginBottom: "0.5rem" }}>افزودن اسلات چرخشی (فقط برای حالت چرخشی)</h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "flex-end" }}>
          <div style={{ flex: "1 1 200px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>تخصیص مرجع</label>
            <select
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", cursor: "pointer" }}
              value={slotForm.assignment}
              onChange={(e) => setSlotForm((s) => ({ ...s, assignment: e.target.value }))}
            >
              <option value="">انتخاب کنید...</option>
              {assignments
                .filter((a: any) => a.mode === "rotational")
                .map((a: any) => {
                  const user = users.find((u: any) => String(u.id) === String(a.operator_user_id));
                  return (
                    <option key={a.id} value={a.id}>
                      {user ? `${user.first_name} ${user.last_name}` : a.operator_user_id} (از {new DateObject({ date: a.start_date, format: "YYYY-MM-DD" }).convert(persian, persian_fa).format("YYYY/MM/DD")})
                    </option>
                  );
                })}
            </select>
          </div>
          <div style={{ flex: "1 1 80px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>ترتیب</label>
            <input
              type="number"
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
              placeholder="1"
              value={slotForm.sequence_no}
              onChange={(e) => setSlotForm((s) => ({ ...s, sequence_no: Number(e.target.value) }))}
            />
          </div>
          <div style={{ flex: "1 1 100px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>ساعت شروع</label>
            <input
              type="number"
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
              placeholder="0"
              value={slotForm.relative_start_hours}
              onChange={(e) => setSlotForm((s) => ({ ...s, relative_start_hours: Number(e.target.value) }))}
            />
          </div>
          <div style={{ flex: "1 1 100px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>ساعت پایان</label>
            <input
              type="number"
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
              placeholder="1"
              value={slotForm.relative_end_hours}
              onChange={(e) => setSlotForm((s) => ({ ...s, relative_end_hours: Number(e.target.value) }))}
            />
          </div>
          <div style={{ flex: "1 1 150px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>ایستگاه کاری</label>
            <select
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", cursor: "pointer" }}
              value={slotForm.target_work_center}
              onChange={(e) => setSlotForm((s) => ({ ...s, target_work_center: e.target.value, target_machine: "" }))}
            >
              <option value="">(بدون ایستگاه)</option>
              {workCenters.map((wc: any) => (
                <option key={wc.id} value={wc.id}>
                  {wc.name} ({wc.code})
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1 1 150px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>دستگاه</label>
            <select
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", cursor: "pointer" }}
              value={slotForm.target_machine}
              onChange={(e) => setSlotForm((s) => ({ ...s, target_machine: e.target.value, target_work_center: "" }))}
            >
              <option value="">(بدون دستگاه)</option>
              {machines.map((m: any) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.code})
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: "0 0 auto" }}>
            <button
              style={{ padding: "8px 16px", borderRadius: "10px", background: "#007270", color: "#FFF", border: "none", fontSize: "0.85rem", fontWeight: 700, cursor: !slotForm.assignment || addSlot.isPending ? "not-allowed" : "pointer", opacity: !slotForm.assignment || addSlot.isPending ? 0.7 : 1 }}
              onClick={() =>
                addSlot.mutate({
                  assignment: slotForm.assignment,
                  sequence_no: slotForm.sequence_no,
                  relative_start: toIsoDuration(slotForm.relative_start_hours),
                  relative_end: toIsoDuration(slotForm.relative_end_hours),
                  target_work_center: slotForm.target_work_center || undefined,
                  target_machine: slotForm.target_machine || undefined,
                })
              }
              disabled={!slotForm.assignment || addSlot.isPending}
            >
              افزودن اسلات
            </button>
          </div>
        </div>
      </section>

      <section className="brand-card-extand" style={{ padding: "1.5rem", border: "1px solid var(--color-border, #E4DCC8)", display: "flex", flexDirection: "column", gap: "1rem" }}>
        <h2 style={{ fontSize: "1.1rem", fontWeight: 800, color: "#007270", marginBottom: "0.5rem" }}>استعلام وضعیت فعال</h2>
        <div style={{ display: "flex", gap: "1rem", alignItems: "flex-end" }}>
          <div style={{ flex: "1 1 200px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>اپراتور</label>
            <select
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", cursor: "pointer" }}
              value={lookup.operator_user_id}
              onChange={(e) => setLookup((s) => ({ ...s, operator_user_id: e.target.value }))}
            >
              <option value="">انتخاب کنید...</option>
              {users.map((u: any) => (
                <option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1 1 200px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>تاریخ</label>
            <DatePicker
              calendar={persian}
              locale={persian_fa}
              calendarPosition="bottom-right"
              portal
              value={lookup.date || ""}
              onChange={(date: DateObject | null) => {
                setLookup((s) => ({ ...s, date: date ? date.format("YYYY-MM-DD") : "" }));
              }}
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", height: "auto" }}
              containerStyle={{ width: "100%" }}
              placeholder="انتخاب تاریخ شمسی..."
            />
          </div>
        </div>
        
        {resolved.length > 0 ? (
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "1rem" }}>
            {resolved.map((r: any, idx: number) => (
              <li key={idx} style={{ padding: "0.75rem 1rem", background: "rgba(0, 114, 112, 0.05)", border: "1px solid rgba(0, 114, 112, 0.15)", borderRadius: "8px", fontSize: "0.9rem", color: "#2B2B2B" }}>
                <strong>[{r.mode}]</strong> ایستگاه / دستگاه: <span style={{ color: "#007270", fontWeight: 700 }}>{r.work_center_code ?? r.machine_code}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p style={{ fontSize: "0.85rem", color: "#888", marginTop: "1rem" }}>اپراتور و تاریخ را برای استعلام انتخاب کنید.</p>
        )}
      </section>
    </div>
  );
}
